import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { tintSafe, transparentizeSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { Line } from '../../../platform/geom/Line';
import { Matrix } from '../../../platform/geom/Matrix';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { Fill } from '../../../ui/Fill';
import { Topic } from '../../packages/topics/Topic';
import { TopicBoundaryAttr } from '../../packages/topics/TopicAssociations';
import { VoteAggregateAttr, VoteRankAttr } from '../../packages/voting/Vote';
import { RelativeTo } from '../entities/Physical';
import { aspect } from './Aspect';
import { RenderVotes } from './RenderAnnotations';
import { RenderTextField } from './RenderField';
import { RenderBorder, RenderBounds, RenderBox } from './RenderHelpers';
import { RenderRankingMedal } from './RenderRankingMedal';
import { VisualBase } from './VisualBase';

/* eslint-disable max-len */
/* eslint-disable no-mixed-operators */
export class TopicVisual extends VisualBase<Topic> {

    @aspect()
    private get fillColor() {
        const { object } = this.props;
        return tintSafe(.1, object.color);
    }

    @aspect()
    private get highlightColor() {
        const { pose, object } = this;
        return (pose.selected || pose.targeted) ? tintSafe(pose.selected ? -.3 : -.15, object.color) : 'transparent';
    }

    private get highlightStyling(): any {
        return { borderColor: this.highlightColor, };
    }

    @aspect()
    private get borderStyling(): any {
        const { object } = this.props;
        return {
            width: object.width,
            height: object.height,
            backgroundColor: this.fillColor,
            borderRadius: '4px',
            // border: object.border,
        };
    }

    public render() {
        const boundary = TopicBoundaryAttr.get(this.object);
        const voteAggr = VoteAggregateAttr.get(this.object);
        const voteRank = VoteRankAttr.get(this.object);
        return <>
            {boundary &&
                <RenderBox transform={this.object.transform(RelativeTo.Model).toCSS()} zIndex={10}>
                    <Boundary boundary={boundary} color={this.object.color} fill={!!voteRank} />
                    <VoteCaption boundary={boundary} color={this.object.color} count={voteAggr} />
                </RenderBox>
            }
            <RenderBounds object={this.object} style={{ zIndex: voteRank ? 2 : 1 }}>
                <RenderBorder pose={this.pose} style={this.borderStyling}>
                    {this.isHighlighted && <Highlight style={this.highlightStyling} />}
                    {!this.isBeingEdited && <RenderTextField object={this.object} name="label" />}
                    <RenderVotes object={this.object} />
                    {voteRank && <RankingMedalOffset boundary={boundary} width={this.object.width} height={this.object.height}>
                        <RenderRankingMedal value={voteRank} show />
                    </RankingMedalOffset>}
                </RenderBorder>
            </RenderBounds>
        </>;
    }
}

const Highlight = styled(Fill)`
    border: solid 2px;
    border-radius: 4px;
`;

const Boundary: React.FC<{ boundary: Polyline, color: string, fill?: boolean }> = ({ boundary, color, fill }) => {

    const area = boundary.bounds.inflate([10, 10]);
    const pathStr = boundary.toRoundedPath('quadratic', 50).toString();

    // const pathString = polygon.toRoundedPath().toPathSyntax();
    const width = `${area.width}px`;
    const height = `${area.height}px`;
    const viewBox = [area.left, area.top, area.width, area.height].join(' ');
    const style = {
        transform: `translate(${area.left}px, ${area.top}px)`
    };

    return <div {...{ style }}>
        <svg {...{ width, height, viewBox, }}>
            <path fill={fill ? transparentizeSafe(.7, color) : 'none'} stroke={color} strokeWidth="3" d={pathStr} />
        </svg>
    </div>;
};

const computeCaptionPosition = (boundary: Polyline) => {

    const size = new Point(140, 30);
    const p1 = boundary.bounds.center();
    const p2 = new Point(p1.x, boundary.top.y);
    const ray = new Line(p1, p2).extend(50);

    const ipsr = boundary.intersectPoints(ray);
    if (!ipsr) return Point.max;
    let [pt] = ipsr.sort((a, b) => b.y - a.y);
    pt = pt.add([0, -size.y / 2]);

    for (let i = 0; i < 10; i++) {
        const area = Rect.fromCenter(pt, size);
        if (!boundary.intersects(area)) break;
        pt = pt.add([0, -5]);
    }

    return pt;
};

// eslint-disable-next-line react/display-name
const VoteCaption = React.memo<{ boundary: Polyline, color: string, count: number | null }>(({ boundary, color, count }) => {
    if (!count) return null;

    const position = computeCaptionPosition(boundary);
    const styling: any = {
        position: 'absolute',
        left: 0,
        top: 0,
        transform: `translate(${position.x}px, ${position.y}px) translate(-50%, -50%)`,
        color,
        fontSize: '28px',
        textShadow: '0 0 1px rgba(0,0,0,0.1)',
        whiteSpace: 'nowrap',
        textAlign: 'center',
        transition: true ? 'transform 150ms ease-out' : '',
    };

    return <div style={styling}>Votes: {count}</div>;
});

// eslint-disable-next-line react/display-name
// const RankingMedalOffset = React.memo<PropsWithChildren<{ boundary: Polyline, width: number, height: number }>>(({ children, boundary, width, height }) => {
const RankingMedalOffset: React.FC<PropsWithChildren<{ boundary?: Polyline, width: number, height: number }>> = ({ children, boundary, width, height }) => {

    // If boundary exists, we want to put the medal in the center of the boundary polyline, but we don't want to overlap the actual element.
    // So, check for overlap and move the medal until there isn't one.  If the boundary doesn't exist, we should just put the medal on the
    // right hand side of the element.

    const startPt = !!boundary && !!boundary.points.length ? boundary.center : new Point(width, height / 2);
    const elmtBounds = Rect.fromSize([width, height]);
    const offsetVec = Point.vector(elmtBounds.center(), startPt).normalize();

    let offsetPt = startPt;

    while (true) {
        const mb = Rect.fromCenter(offsetPt, 65);
        if (mb.intersects(elmtBounds)) {
            offsetPt = offsetPt.add(offsetVec.multiply(10));
        }
        else {
            break;
        }
    }

    const styling = {
        transform: Matrix.translate(offsetPt).toCSS(),
        transition: 'transform 500ms ease-out',
        position: 'absolute',
    };

    return <div style={styling as any}>{children}</div>;
};
